import { FeedbackRequest } from '../types/feedback';
import { renderRichText } from './richText';
import { formatDateInIST, getEmployeeSubmittedDate, getManagerCompletedDate } from './mappers';

// Opens the generated PDF/print HTML in a new window and triggers print.
// Auto-closes the popup once the print dialog is dismissed (or after a fallback
// timeout) so a leftover blank window can't keep grabbing focus and make the
// app feel "stuck" after the user prints/cancels and navigates away.
export const printHtmlInNewWindow = (html: string): void => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  printWindow.document.write(html);
  printWindow.document.close();

  let closed = false;
  const closeOnce = () => {
    if (closed) return;
    closed = true;
    printWindow.close();
  };

  printWindow.onafterprint = closeOnce;
  // Fallback in case `afterprint` doesn't fire (varies by browser).
  setTimeout(closeOnce, 60000);

  printWindow.onload = () => {
    setTimeout(() => printWindow.print(), 250);
  };
};

interface CorePrioritiesData {
  year?: number;
  priorities?: string;
  goals?: CorePriorityGoalData[];
  managerName?: string;
  modifiedDate?: string;
}

interface CorePriorityGoalData {
  goal?: string;
  description?: string;
  category?: string;
  weightage?: number;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Renders rich-text fields (answers, manager feedback, PIP reason, etc.) as real HTML
// instead of escaping them — these fields store HTML from RichTextEditor.
const formatText = (value?: string): string => {
  if (!value || !value.trim()) return 'Not provided';
  return renderRichText(value).__html || 'Not provided';
};

const formatDate = (value?: string): string => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : formatDateInIST(date);
};

const formatGoalsHtml = (goals?: CorePriorityGoalData[]): string => {
  if (!goals || goals.length === 0) return '';

  return goals.map((goal, index) => {
    const title = goal.goal?.trim() ? escapeHtml(goal.goal) : `Goal ${index + 1}`;
    const description = goal.description?.trim() ? formatText(goal.description) : 'Not provided';
    const category = goal.category?.trim() ? escapeHtml(goal.category) : 'N/A';
    const weightage = typeof goal.weightage === 'number' ? goal.weightage : 'N/A';

    return `
      <div class="goal-card">
        <div class="goal-head">
          <div>
            <div class="goal-index">Goal ${index + 1}</div>
            <div class="goal-title">${title}</div>
          </div>
          <div class="goal-weight">Weightage: ${weightage}</div>
        </div>
        <div class="goal-desc">${description}</div>
        <div class="goal-meta-grid">
          <div class="goal-meta-item"><strong>Category:</strong> ${category}</div>
        </div>
      </div>
    `;
  }).join('');
};

export const generateConnectPDFContent = (
  feedbackRequest: FeedbackRequest,
  corePriorities: CorePrioritiesData | null
): string => {
  const currentDate = formatDateInIST(new Date());
  const reportTitle = feedbackRequest.connectName || 'Connect Report';
  const overallComments = (feedbackRequest as FeedbackRequest & { overallComments?: string }).overallComments;

  const empSubmittedStr = getEmployeeSubmittedDate(feedbackRequest.timeline) || (feedbackRequest.status !== 'draft' ? feedbackRequest.requestDate : undefined);
  const employeeDateDisplay = empSubmittedStr ? formatDateInIST(empSubmittedStr) : '';

  const mgrCompletedStr = getManagerCompletedDate(feedbackRequest.timeline) || (feedbackRequest.status === 'completed' ? (feedbackRequest as any).updatedAt : undefined);
  const managerDateDisplay = mgrCompletedStr ? formatDateInIST(mgrCompletedStr) : '';

  const responses = feedbackRequest.answers && feedbackRequest.answers.length > 0
    ? feedbackRequest.answers.map((answer, idx) => `
        <section class="qa-card">
          <div class="qa-head">
            <span class="index-pill">${idx + 1}</span>
            <h3 class="question">${formatText(answer.question || `Question ${idx + 1}`)}</h3>
          </div>
          <div class="response-block employee">
            <div class="response-header">
              <div class="response-label">Employee Response</div>
              ${employeeDateDisplay ? `<div class="response-date">${employeeDateDisplay}</div>` : ''}
            </div>
            <div class="response-text">${formatText(answer.answer)}</div>
          </div>
          ${answer.managerFeedback && answer.managerFeedback.trim() ? `
            <div class="response-block manager">
              <div class="response-header">
                <div class="response-label">Manager Feedback</div>
                ${managerDateDisplay ? `<div class="response-date manager-date">${managerDateDisplay}</div>` : ''}
              </div>
              <div class="response-text">${formatText(answer.managerFeedback)}</div>
            </div>
          ` : `
            <div class="response-block empty">
              <div class="response-header">
                <div class="response-label">Manager Feedback</div>
              </div>
              <div class="response-text muted">No manager feedback provided.</div>
            </div>
          `}
        </section>
      `).join('')
    : '<p class="empty-message">No feedback responses available.</p>';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Connect Report - ${escapeHtml(reportTitle)}</title>
      <style>
        @page { size: A4; margin: 14mm; }
        @media print {
          body { margin: 0; }
        }
        * { box-sizing: border-box; }
        body {
          margin: 0;
          color: #0f172a;
          background: #ffffff;
          font-family: "Segoe UI", Tahoma, Arial, sans-serif;
          line-height: 1.5;
          font-size: 12px;
        }
        .sheet {
          max-width: 210mm;
          margin: 0 auto;
        }
        .hero {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 45%, #db2777 100%);
          color: #ffffff;
          border-radius: 14px;
          padding: 16px 20px;
          margin-bottom: 10px;
        }
        .eyebrow {
          font-size: 11px;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          opacity: 0.85;
          margin-bottom: 8px;
        }
        .hero h1 {
          margin: 0 0 8px 0;
          font-size: 22px;
          line-height: 1.2;
          font-weight: 700;
        }
        .meta-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px 16px;
        }
        .meta-item {
          font-size: 11px;
          opacity: 0.95;
        }
        .meta-item strong {
          display: block;
          font-size: 12px;
          opacity: 1;
          margin-top: 1px;
        }
        .section {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          margin-bottom: 10px;
          overflow: hidden;
        }
        .section-head {
          background: #f1f5f9;
          padding: 10px 14px;
          border-bottom: 1px solid #e2e8f0;
        }
        .section-title {
          margin: 0;
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: 0.2px;
        }
        .section-body {
          padding: 10px 14px;
        }
        .priorities-text {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 12px;
          color: #334155;
          line-height: 1.7;
        }
        .goal-card {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 8px 10px;
          margin-bottom: 8px;
          background: #ffffff;
          page-break-inside: avoid;
        }
        .goal-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 8px;
        }
        .goal-index {
          color: #64748b;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          font-weight: 700;
        }
        .goal-title {
          color: #0f172a;
          font-size: 13px;
          font-weight: 700;
        }
        .goal-weight {
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          border-radius: 999px;
          padding: 3px 8px;
          font-size: 10px;
          color: #334155;
          font-weight: 700;
          white-space: nowrap;
        }
        .goal-desc {
          color: #334155;
          line-height: 1.65;
          margin-bottom: 8px;
        }
        .goal-meta-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }
        .goal-meta-item {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 6px 8px;
          color: #475569;
          font-size: 11px;
        }
        .mini-meta {
          margin-top: 10px;
          color: #64748b;
          font-size: 11px;
        }
        .qa-card {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 10px;
          margin-bottom: 8px;
          background: #ffffff;
          page-break-inside: avoid;
        }
        .qa-head {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 10px;
        }
        .index-pill {
          min-width: 22px;
          height: 22px;
          border-radius: 999px;
          background: #fce7f3;
          color: #be185d;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          border: 1px solid #fbcfe8;
        }
        .question {
          margin: 2px 0 0 0;
          font-size: 13px;
          line-height: 1.45;
          color: #0f172a;
        }
        .response-block {
          border-radius: 10px;
          padding: 8px 10px;
          margin-top: 6px;
        }
        .response-block.employee {
          background: #f0fdfa;
          border: 1px solid #99f6e4;
        }
        .response-block.manager {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
        }
        .response-block.empty {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
        }
        .response-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 3px;
        }
        .response-label {
          color: #334155;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          font-weight: 700;
          margin-bottom: 0;
        }
        .response-date {
          color: #64748b;
          font-size: 10px;
        }
        .response-date.manager-date {
          color: #60a5fa;
        }
        .response-text {
          color: #1e293b;
          line-height: 1.7;
          white-space: normal;
          word-wrap: break-word;
        }
        .response-text img {
          max-width: 100%;
          height: auto;
          border-radius: 6px;
          margin: 4px 0;
        }
        .response-text table {
          border-collapse: collapse;
          margin: 6px 0;
          max-width: 100%;
        }
        .response-text table td,
        .response-text table th {
          border: 1px solid #cbd5e1;
          padding: 5px 8px;
        }
        .muted { color: #64748b; font-style: italic; }
        .empty-message {
          text-align: center;
          color: #64748b;
          padding: 18px;
          margin: 0;
          border: 1px dashed #cbd5e1;
          border-radius: 10px;
        }
        .pip-section {
          border: 2px solid #f87171;
          border-radius: 12px;
          margin-bottom: 10px;
          overflow: hidden;
          page-break-inside: avoid;
        }
        .pip-head {
          background: #fef2f2;
          padding: 10px 14px;
          border-bottom: 2px solid #f87171;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .pip-icon {
          color: #dc2626;
          font-size: 16px;
        }
        .pip-title {
          margin: 0;
          font-size: 13px;
          font-weight: 700;
          color: #991b1b;
          letter-spacing: 0.2px;
        }
        .pip-body {
          padding: 10px 14px;
          background: #fff5f5;
        }
        .pip-detail {
          display: inline-block;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 6px 12px;
          margin-right: 10px;
          margin-bottom: 8px;
          font-size: 11px;
          color: #991b1b;
        }
        .pip-detail strong {
          font-weight: 700;
        }
        .pip-reason {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 10px;
          padding: 10px 12px;
          margin-top: 8px;
          color: #7f1d1d;
          line-height: 1.6;
        }
        .pip-reason-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          font-weight: 700;
          color: #991b1b;
          margin-bottom: 4px;
        }
        .footer {
          margin-top: 12px;
          border-top: 1px solid #e2e8f0;
          padding-top: 6px;
          text-align: center;
          color: #64748b;
          font-size: 10px;
        }
      </style>
    </head>
    <body>
      <main class="sheet">
        <header class="hero">
          <div class="eyebrow">Connect Performance Document</div>
          <h1>${escapeHtml(reportTitle)}</h1>
          <div class="meta-grid">
            <div class="meta-item">Employee<strong>${escapeHtml(feedbackRequest.employeeName || 'N/A')}</strong></div>
            <div class="meta-item">Manager<strong>${escapeHtml(feedbackRequest.managerName || 'N/A')}</strong></div>
            <div class="meta-item">Review Year<strong>${feedbackRequest.year || 'N/A'}</strong></div>
            <div class="meta-item">Generated On<strong>${currentDate}</strong></div>
          </div>
        </header>

        ${corePriorities ? `
          <section class="section">
            <div class="section-head">
              <h2 class="section-title">Core Priorities (${corePriorities.year || feedbackRequest.year || 'N/A'})</h2>
            </div>
            <div class="section-body">
              ${corePriorities.goals && corePriorities.goals.length > 0
                ? formatGoalsHtml(corePriorities.goals)
                : `<div class="priorities-text">${formatText(corePriorities.priorities)}</div>`}
              <div class="mini-meta">
                Set by ${escapeHtml(corePriorities.managerName || feedbackRequest.managerName || 'N/A')} |
                Last updated ${formatDate(corePriorities.modifiedDate)}
              </div>
            </div>
          </section>
        ` : ''}

        ${feedbackRequest.pip && feedbackRequest.pip.isOnPip ? `
          <section class="pip-section">
            <div class="pip-head">
              <span class="pip-icon">⚠</span>
              <h2 class="pip-title">Performance Improvement Plan (PIP)</h2>
            </div>
            <div class="pip-body">
              <p style="margin:0 0 10px 0;color:#991b1b;font-size:12px;">
                This employee has been placed on a Performance Improvement Plan.
              </p>
              <div>
                ${feedbackRequest.pip.pipStartDate ? `
                  <span class="pip-detail">
                    <strong>Start Date:</strong> ${formatDate(feedbackRequest.pip.pipStartDate)}
                  </span>
                ` : ''}
                ${feedbackRequest.pip.pipEndDate ? `
                  <span class="pip-detail">
                    <strong>End Date:</strong> ${formatDate(feedbackRequest.pip.pipEndDate)}
                  </span>
                ` : ''}
              </div>
              ${feedbackRequest.pip.pipReason ? `
                <div class="pip-reason">
                  <div class="pip-reason-label">Reason</div>
                  <div>${formatText(feedbackRequest.pip.pipReason)}</div>
                </div>
              ` : ''}
            </div>
          </section>
        ` : ''}

        <section class="section">
          <div class="section-head">
            <h2 class="section-title">Question Responses</h2>
          </div>
          <div class="section-body">
            ${responses}
          </div>
        </section>

        <section class="section">
          <div class="section-head">
            <h2 class="section-title">Manager's Overall Comments</h2>
          </div>
          <div class="section-body">
            <div class="response-block manager">
              <div class="response-text">${formatText(overallComments)}</div>
            </div>
          </div>
        </section>

        <footer class="footer">
          <p>This report was automatically generated by the Connect Management System.</p>
          <p>Confidential - Internal Use Only</p>
        </footer>
      </main>
    </body>
    </html>
  `;
};
